import { Checkbox, FormControlLabel, FormGroup, Menu, MenuItem, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import CreateIcon from "@mui/icons-material/Create";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Job } from "generated/graphql";
import Modal from "modules/Modal/Modal";
import useModalState from "hooks/useModalState";
import { useCallback, useState } from "react";
import { useForm, Controller, FormProvider, useFieldArray } from "react-hook-form";
import useUpdateJobChecklistItems from "hooks/jobs/useUpdateJobChecklistItems";
import useGetOrganizationProposalChecklistItems from "hooks/organization/useGetOrganizationProposalChecklistItems";
import CreateChecklistItemModal from "modules/proposals/CreateChecklistItemModal";

export default function JobChecklist({ job }: { job: Job }) {
  const { open, onClose, onOpen } = useModalState();

  return (
    <>
      <MDButton variant="gradient" color="success" onClick={onOpen}>
        Job Checklist
      </MDButton>
      <Modal open={open} onClose={onClose} styles={{ overflow: "auto", width: "500px" }}>
        <MDBox p={3}>
          <MDTypography variant="h5" fontWeight="medium" mb={3}>
            Manage Job Checklists
          </MDTypography>
          <JobChecklistContent job={job} onClose={onClose} />
        </MDBox>
      </Modal>
    </>
  );
}

type FormValues = {
  phases: Record<string, boolean>;
  items: Record<string, Record<string, boolean>>;
  groupedItems: Record<string, { phase: any; items: any[] }>;
  newItems: Array<{
    checklistItemId: string;
    name: string;
    phaseId: string;
    isCompleted: boolean;
  }>;
};

const getDefaultValues = (job: any, organizationChecklistItems?: any[]): FormValues => {
  const phases: Record<string, boolean> = {};
  const items: Record<string, Record<string, boolean>> = {};
  const groupedItems: Record<string, { phase: any; items: any[] }> = {};
  const newItems: Array<{
    checklistItemId: string;
    name: string;
    phaseId: string;
    isCompleted: boolean;
  }> = [];

  const checklistItemsMap = new Map();
  organizationChecklistItems?.forEach((item) => {
    checklistItemsMap.set(item.id, item);
  });

  if (job?.jobPhases) {
    job.jobPhases.forEach((phase: any) => {
      groupedItems[phase.id] = {
        phase,
        items: [],
      };

      // phase completion status
      phases[phase.id] = true;
      items[phase.id] = {};
    });
  }

  if (job?.checklistItems) {
    job.checklistItems.forEach((item: any) => {
      const phaseId = item.jobPhaseId;

      if (groupedItems[phaseId]) {
        const itemWithDetails = {
          ...item,
          checklistItem: checklistItemsMap.get(item.checklistItemId) || {
            id: item.checklistItemId,
            name: "",
          },
        };

        groupedItems[phaseId].items.push(itemWithDetails);

        const isCompleted = !!item.isCompleted;
        items[phaseId][item.id] = isCompleted;
        if (!isCompleted) {
          phases[phaseId] = false;
        }
      }
    });
  }

  return {
    phases,
    items,
    groupedItems,
    newItems,
  };
};

function JobChecklistContent({ job, onClose }: { job: Job; onClose: () => void }) {
  const { data: organizationChecklistItems, loading: loadingChecklistItems } =
    useGetOrganizationProposalChecklistItems();

  const methods = useForm<FormValues>({
    defaultValues: getDefaultValues(job, organizationChecklistItems),
    values: getDefaultValues(job, organizationChecklistItems),
  });

  const {
    fields: newItemFields,
    append,
    remove,
  } = useFieldArray({
    control: methods.control,
    name: "newItems",
  });

  const [anchorEl, setAnchorEl] = useState<Record<string, HTMLElement | null>>({});
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creatingItemForPhaseId, setCreatingItemForPhaseId] = useState<string | null>(null);

  const [updateJobChecklistItems, { loading: updating }] = useUpdateJobChecklistItems();
  const { control, setValue, handleSubmit, watch } = methods;

  const newItems = watch("newItems");
  const groupedItems = watch("groupedItems");

  const handlePhaseSelectAll = useCallback(
    (phaseId: string, checked: boolean) => {
      setValue(`phases.${phaseId}`, checked);

      const itemsForPhase: Record<string, boolean> = {};

      if (groupedItems[phaseId]?.items) {
        groupedItems[phaseId].items.forEach((item: any) => {
          itemsForPhase[item.id] = checked;
        });
      }

      newItems?.forEach((newItem, index) => {
        if (newItem.phaseId === phaseId) {
          itemsForPhase[`new_${newItem.checklistItemId}_${index}`] = checked;
        }
      });

      setValue(`items.${phaseId}`, itemsForPhase);
    },
    [setValue, newItems, groupedItems]
  );

  const handleItemChange = useCallback(
    (phaseId: string, itemId: string, checked: boolean, onChange: (value: boolean) => void) => {
      onChange(checked);

      const updatedValues = {
        ...methods.getValues(`items.${phaseId}`),
        [itemId]: checked,
      };

      const existingItems = groupedItems[phaseId]?.items || [];
      const allExistingSelected = existingItems.every((item: any) =>
        item.id === itemId ? checked : updatedValues[item.id]
      );

      const newItemsForPhase = newItems?.filter((newItem) => newItem.phaseId === phaseId) || [];
      const allNewItemsSelected = newItemsForPhase.every((newItem, index) => {
        const newItemKey = `new_${newItem.checklistItemId}_${index}`;
        return newItemKey === itemId ? checked : updatedValues[newItemKey];
      });

      const allSelected = allExistingSelected && allNewItemsSelected;

      setValue(`phases.${phaseId}`, allSelected);
    },
    [setValue, methods, newItems, groupedItems]
  );

  const handleRemoveItem = useCallback(
    (phaseId: string, itemId: string, isNewItem: boolean = false, itemIndex?: number) => {
      const currentItems = methods.getValues(`items.${phaseId}`);
      const { [itemId]: removed, ...remainingItems } = currentItems;
      setValue(`items.${phaseId}`, remainingItems);

      if (isNewItem && itemIndex !== undefined) {
        remove(itemIndex);
      } else {
        const currentGroupedItems = methods.getValues("groupedItems");
        const updatedGroupedItems = {
          ...currentGroupedItems,
          [phaseId]: {
            ...currentGroupedItems[phaseId],
            items: currentGroupedItems[phaseId].items.filter((item: any) => item.id !== itemId),
          },
        };
        setValue("groupedItems", updatedGroupedItems);
      }

      const existingItems = isNewItem
        ? groupedItems[phaseId]?.items || []
        : methods.getValues("groupedItems")[phaseId]?.items || [];

      const remainingNewItems =
        isNewItem && itemIndex !== undefined
          ? newItems.filter((_, index) => index !== itemIndex && _.phaseId === phaseId)
          : newItems?.filter((newItem) => newItem.phaseId === phaseId) || [];

      const allExistingSelected = existingItems.every((item: any) => remainingItems[item.id]);
      const allNewItemsSelected = remainingNewItems.every((newItem, index) => {
        const newItemKey = `new_${newItem.checklistItemId}_${index}`;
        return remainingItems[newItemKey];
      });

      const allSelected =
        (existingItems.length === 0 || allExistingSelected) &&
        (remainingNewItems.length === 0 || allNewItemsSelected);
      setValue(`phases.${phaseId}`, allSelected);
    },
    [setValue, methods, remove, groupedItems, newItems]
  );

  const handleOpenMenu = (phaseId: string, event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl({
      ...anchorEl,
      [phaseId]: event.currentTarget,
    });
  };

  const handleCloseMenu = (phaseId: string) => {
    setAnchorEl({
      ...anchorEl,
      [phaseId]: null,
    });
  };

  const handleAddChecklistItem = (phaseId: string, item: { id: string; name: string }) => {
    const existingItems = groupedItems[phaseId]?.items || [];
    const itemExists = existingItems.some(
      (checkItem: any) => checkItem.checklistItem?.id === item.id
    );

    if (!itemExists) {
      append({
        checklistItemId: item.id,
        name: item.name,
        phaseId: phaseId,
        isCompleted: false,
      });

      const newItemIndex = newItems.length;
      setValue(`items.${phaseId}.new_${item.id}_${newItemIndex}`, false);
    }

    handleCloseMenu(phaseId);
  };

  const handleOpenCreateModal = (phaseId: string) => {
    setCreatingItemForPhaseId(phaseId);
    setCreateModalOpen(true);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setCreatingItemForPhaseId(null);
  };

  const handleCreateSuccess = (newItem: { id: string; name: string }) => {
    if (creatingItemForPhaseId) {
      append({
        checklistItemId: newItem.id,
        name: newItem.name,
        phaseId: creatingItemForPhaseId,
        isCompleted: false,
      });
      const newItemIndex = methods.getValues("newItems").length - 1;
      setValue(`items.${creatingItemForPhaseId}.new_${newItem.id}_${newItemIndex}`, false);
    }
  };

  const onSubmit = async (data: FormValues) => {
    const checklistUpdates: any[] = [];

    Object.entries(data.items).forEach(([phaseId, items]) => {
      const existingItems = groupedItems[phaseId]?.items || [];
      existingItems.forEach((item) => {
        if (items[item.id] !== undefined) {
          checklistUpdates.push({
            id: item.id,
            isCompleted: items[item.id],
            checklistItemId: item.checklistItemId,
            jobPhaseId: phaseId,
          });
        }
      });

      data.newItems.forEach((newItem, index) => {
        const newItemKey = `new_${newItem.checklistItemId}_${index}`;
        if (newItem.phaseId === phaseId && items[newItemKey] !== undefined) {
          checklistUpdates.push({
            checklistItemId: newItem.checklistItemId,
            isCompleted: items[newItemKey],
            jobPhaseId: phaseId,
          });
        }
      });
    });

    await updateJobChecklistItems({
      variables: {
        input: {
          id: job.id,
          checklistItems: {
            upsert: checklistUpdates,
          },
        },
      },
    });
    onClose();
  };

  if (!job.jobPhases || job.jobPhases.length === 0) {
    return (
      <MDBox p={2} textAlign="center">
        <MDTypography variant="body2" color="text">
          No job phases found.
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <FormProvider {...methods}>
      <MDBox component="form" onSubmit={handleSubmit(onSubmit)}>
        {Object.entries(groupedItems).map(([phaseId, { phase, items }]) => (
          <MDBox key={phaseId} mb={4}>
            <MDBox
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={1}
              px={2}
              sx={{ backgroundColor: "#f8f9fa", borderRadius: "8px" }}
            >
              <MDTypography variant="h6" fontWeight="medium">
                {phase.productName}
              </MDTypography>
              <FormGroup>
                <Controller
                  control={control}
                  name={`phases.${phaseId}`}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value || false}
                          onChange={(e) => {
                            field.onChange(e.target.checked);
                            handlePhaseSelectAll(phaseId, e.target.checked);
                          }}
                        />
                      }
                      label="Select All"
                    />
                  )}
                />
              </FormGroup>
            </MDBox>

            <MDBox>
              {items && items.length > 0 ? (
                <>
                  {items.map((item: any) => (
                    <FormGroup key={item.id}>
                      <MDBox display="flex" alignItems="center" justifyContent="space-between">
                        <Controller
                          control={control}
                          name={`items.${phaseId}.${item.id}`}
                          render={({ field }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={field.value || false}
                                  onChange={(e) => {
                                    handleItemChange(
                                      phaseId,
                                      item.id,
                                      e.target.checked,
                                      field.onChange
                                    );
                                  }}
                                />
                              }
                              label={
                                <MDTypography
                                  variant="body2"
                                  sx={{
                                    textDecoration: field.value ? "line-through" : "none",
                                    color: field.value ? "text.secondary" : "text.primary",
                                  }}
                                >
                                  {item.checklistItem?.name || ""}
                                </MDTypography>
                              }
                            />
                          )}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveItem(phaseId, item.id)}
                          sx={{ ml: 1 }}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </MDBox>
                    </FormGroup>
                  ))}
                  {newItems
                    .filter((newItem) => newItem.phaseId === phaseId)
                    .map((newItem, index) => (
                      <FormGroup key={`${newItem.checklistItemId}_${index}`}>
                        <MDBox display="flex" alignItems="center" justifyContent="space-between">
                          <Controller
                            control={control}
                            name={`items.${phaseId}.new_${newItem.checklistItemId}_${index}`}
                            render={({ field }) => (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={field.value || false}
                                    onChange={(e) => {
                                      handleItemChange(
                                        phaseId,
                                        `new_${newItem.checklistItemId}_${index}`,
                                        e.target.checked,
                                        field.onChange
                                      );
                                    }}
                                  />
                                }
                                label={
                                  <MDTypography
                                    variant="body2"
                                    sx={{
                                      textDecoration: field.value ? "line-through" : "none",
                                      color: field.value ? "text.secondary" : "text.primary",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    {newItem.name}
                                  </MDTypography>
                                }
                              />
                            )}
                          />
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleRemoveItem(
                                phaseId,
                                `new_${newItem.checklistItemId}_${index}`,
                                true,
                                index
                              )
                            }
                            sx={{ ml: 1 }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </MDBox>
                      </FormGroup>
                    ))}
                </>
              ) : (
                <>
                  <MDTypography variant="body2" color="text" fontStyle="italic">
                    No checklist items for this phase.
                  </MDTypography>
                  {newItems
                    .filter((newItem) => newItem.phaseId === phaseId)
                    .map((newItem, index) => (
                      <FormGroup key={`${newItem.checklistItemId}_${index}`}>
                        <MDBox display="flex" alignItems="center" justifyContent="space-between">
                          <Controller
                            control={control}
                            name={`items.${phaseId}.new_${newItem.checklistItemId}_${index}`}
                            render={({ field }) => (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={field.value || false}
                                    onChange={(e) => {
                                      handleItemChange(
                                        phaseId,
                                        `new_${newItem.checklistItemId}_${index}`,
                                        e.target.checked,
                                        field.onChange
                                      );
                                    }}
                                  />
                                }
                                label={
                                  <MDTypography
                                    variant="body2"
                                    sx={{
                                      textDecoration: field.value ? "line-through" : "none",
                                      color: field.value ? "text.secondary" : "text.primary",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    {newItem.name}
                                  </MDTypography>
                                }
                              />
                            )}
                          />
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleRemoveItem(
                                phaseId,
                                `new_${newItem.checklistItemId}_${index}`,
                                true,
                                index
                              )
                            }
                            sx={{ ml: 1 }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </MDBox>
                      </FormGroup>
                    ))}
                </>
              )}
            </MDBox>

            <MDBox display="flex" justifyContent="flex-start" alignItems="center" mt={2}>
              <MDButton
                variant="text"
                color="info"
                size="small"
                sx={{
                  fontSize: "0.75rem",
                  boxShadow: "none",
                  padding: "0.3rem 0.5rem",
                }}
                onClick={(e) => handleOpenMenu(phaseId, e)}
                disabled={loadingChecklistItems}
              >
                <AddIcon sx={{ mr: 0.5, fontSize: "1rem" }} /> Add item
              </MDButton>
              <Menu
                anchorEl={anchorEl[phaseId]}
                open={Boolean(anchorEl[phaseId])}
                onClose={() => handleCloseMenu(phaseId)}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleOpenCreateModal(phaseId);
                    handleCloseMenu(phaseId);
                  }}
                  sx={{
                    backgroundColor: "action.hover",
                    borderRadius: 1,
                    fontWeight: "medium",
                    mx: 1,
                    my: 0.5,
                    "&:hover": {
                      backgroundColor: "action.selected",
                    },
                  }}
                >
                  <AddIcon sx={{ mr: 1, fontSize: 20 }} />
                  Create new item
                </MenuItem>
                {organizationChecklistItems
                  ?.slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((item) => (
                    <MenuItem
                      key={item.id}
                      onClick={() => handleAddChecklistItem(phaseId, item)}
                      disabled={
                        groupedItems[phaseId]?.items?.some(
                          (checkItem: any) => checkItem.checklistItem?.id === item.id
                        ) ||
                        newItems.some(
                          (newItem) =>
                            newItem.checklistItemId === item.id && newItem.phaseId === phaseId
                        )
                      }
                    >
                      {item.name}
                    </MenuItem>
                  ))}
                {organizationChecklistItems?.length === 0 && !loadingChecklistItems && (
                  <MenuItem disabled>No checklist items available</MenuItem>
                )}
                {loadingChecklistItems && <MenuItem disabled>Loading items...</MenuItem>}
              </Menu>
            </MDBox>
          </MDBox>
        ))}

        <MDBox display="flex" justifyContent="flex-end" mt={4}>
          <MDButton
            variant="contained"
            color="success"
            type="submit"
            size="small"
            disabled={updating}
          >
            {updating ? "Saving..." : "Save Changes"}
          </MDButton>
        </MDBox>
      </MDBox>
      <CreateChecklistItemModal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        onSuccess={handleCreateSuccess}
      />
    </FormProvider>
  );
}
